import React from "react";

/**
 * UploadProgressPie — donut/pie indicator with percentage in the center.
 * - percent: 0..100 (null/undefined ⇒ indeterminate spinning ring)
 * - size:    diameter in px
 * - label:   optional small caption under the pie (e.g. "Caricamento" / "AI in lavorazione")
 *
 * Used by ImageUpload (client-side compression progress) and the PDF menu import
 * (multipart upload progress + indeterminate AI processing phase).
 */
export default function UploadProgressPie({
  percent = null,
  size = 72,
  stroke = 7,
  label = "",
  variant = "cyan",
  testId = "upload-pie",
}) {
  const indeterminate = percent === null || percent === undefined;
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = indeterminate ? c * 0.3 : c * (pct / 100);
  const color = variant === "yellow" ? "#FFD600" : variant === "violet" ? "#7C3AED" : "#00E5FF";

  return (
    <div className="flex flex-col items-center justify-center gap-1" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={indeterminate ? "animate-spin" : ""}
          style={indeterminate ? { animationDuration: "1.2s" } : undefined}
        >
          {/* Track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#1f2937"
            strokeWidth={stroke}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: indeterminate ? "none" : "stroke-dasharray .25s ease" }}
          />
        </svg>
        {!indeterminate && (
          <div
            className="absolute inset-0 flex items-center justify-center font-mono font-bold"
            style={{ color, fontSize: size * 0.28 }}
            data-testid={`${testId}-value`}
          >
            {Math.round(pct)}%
          </div>
        )}
        {indeterminate && (
          <div
            className="absolute inset-0 flex items-center justify-center font-mono"
            style={{ color, fontSize: size * 0.22 }}
            data-testid={`${testId}-indeterminate`}
          >
            …
          </div>
        )}
      </div>
      {label && (
        <div className="font-mono uppercase tracking-widest text-[9px] text-zinc-400 text-center">
          {label}
        </div>
      )}
    </div>
  );
}
